import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from '../Icon/index.js';
import calcProgress from '../lib/calcProgress.js';

function ContentIcon({
  content,
  user,
  size,
  noLink,
  trackEvent,
  onClick,
  locks
}) {
  const calcLockOffset = () => {
    const offsets = {};
    if (size === "small") {
      offsets.bottom = "-0.25rem";
      offsets.right = "-0.75rem";
    } else {
      offsets.bottom = "-0.25rem";
      offsets.right = "-0.75rem";
    }
    return offsets;
  };
  const calcIconOffset = () => {
    const offsets = {};
    if (content.type === "superbadge" && size !== "small") {
      offsets.top = "-0.25rem";
      offsets.right = "-0.5rem";
      return offsets;
    }
    if (content.trailmix) {
      offsets.top = "-0.75rem";
      offsets.right = "-0.75rem";
      return offsets;
    }
    if (size === "small") {
      offsets.top = "-0.65rem";
      offsets.right = "-0.85rem";
    } else {
      offsets.top = "-0.25rem";
      offsets.right = "-0.75rem";
    }
    return offsets;
  };
  const renderImageCheck = () => {
    const offsets = calcIconOffset();
    const iconPositionStyles = {
      position: "absolute",
      top: offsets.top,
      right: offsets.right,
      border: "3px solid white",
      borderRadius: "100%"
    };
    const iconClassNames = cx("tds-bg_success slds-show", {
      "slds-p-around_xx-small": size === "small"
    });
    const progress = calcProgress(user, content);
    const completedTrail = content.type === "trail" && progress === 100;
    if (user.finishedAt || completedTrail) {
      return /*#__PURE__*/React.createElement("div", {
        style: iconPositionStyles
      }, /*#__PURE__*/React.createElement(Icon, {
        name: "check",
        assistiveText: {
          label: "complete"
        },
        category: "action",
        size: "x-small",
        containerClassName: iconClassNames
      }));
    }
    return null;
  };
  const renderImageLocked = () => {
    if (!locks) {
      return null;
    }
    if (content.type !== "superbadge") {
      return null;
    }
    if (size === "small") {
      return null;
    }
    if (user.finishedAt) {
      return null;
    }
    const offsets = calcLockOffset();
    const iconPositionStyles = {
      position: "absolute",
      bottom: offsets.bottom,
      right: offsets.right,
      border: "3px solid white",
      borderRadius: "100%"
    };
    let name = "lock";
    let label = "locked";
    let bgClassName = "tds-bg_meteorite";
    if (user.unlocked) {
      name = "unlock";
      label = "unlocked";
      bgClassName = "tds-bg_ocean";
    }
    const iconClassNames = cx(`slds-icon_container slds-show slds-icon_container_circle ${bgClassName}`, {
      "slds-p-around_xx-small": size === "small"
    });
    return /*#__PURE__*/React.createElement("div", {
      style: iconPositionStyles
    }, /*#__PURE__*/React.createElement(Icon, {
      name: name,
      assistiveText: {
        label
      },
      category: "utility",
      size: "x-small",
      containerClassName: iconClassNames,
      className: "tds-fill_white"
    }));
  };
  const imageSizeMap = {
    small: "60px",
    large: "90px"
  };
  const imageStyles = () => {
    const s = {
      height: imageSizeMap[size],
      width: imageSizeMap[size]
    };
    if (content.type === "trail") {
      return {
        ...s,
        ...{
          backgroundColor: `${content.color}`,
          borderRadius: "0.5rem",
          padding: "0.5rem"
        }
      };
    }
    if (content.type === "trailmix") {
      return {
        ...s,
        borderRadius: "0.5rem"
      };
    }
    return s;
  };
  const handleClick = () => {
    onClick();
    trackEvent("Clicked", {
      ...content
    });
  };
  const DivOrAnchor = noLink ? "div" : "a";
  const href = noLink ? undefined : content.path;
  const fns = {};
  if (!noLink) {
    fns.onClick = handleClick;
  }
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(DivOrAnchor, Object.assign({
    href: href,
    "aria-hidden": true,
    tabIndex: "-1",
    className: "slds-is-relative slds-show_inline-block"
  }, fns), /*#__PURE__*/React.createElement("img", {
    src: content.image,
    alt: "",
    style: imageStyles()
  }), renderImageCheck(), renderImageLocked()));
}
ContentIcon.displayName = "ContentIcon";
ContentIcon.defaultProps = {
  user: {},
  content: null,
  size: "large",
  noLink: false,
  locks: true,
  trackEvent: () => {},
  onClick: () => {}
};
ContentIcon.propTypes = {
  /**
   * Content information.
   */
  content: PropTypes.shape({
    color: PropTypes.string,
    image: PropTypes.string.isRequired,
    path: PropTypes.string,
    trailmix: PropTypes.object,
    type: PropTypes.string
  }),
  /**
   * User information.
   */
  user: PropTypes.shape({
    finishedAt: PropTypes.string,
    unlocked: PropTypes.bool
  }),
  /**
   * Omit for regular size. Use "small" as the value for a small icon.
   */
  size: PropTypes.string,
  /**
   * Avoid the icon being a link as well. `content.path` is no longer considered.
   */
  noLink: PropTypes.bool,
  /**
   * Callback when icon is clicked with two arguments: "Clicked" and the `content` prop object properties.
   */
  trackEvent: PropTypes.func,
  /**
   * Callback when icon is clicked.
   */

  onClick: PropTypes.func,
  locks: PropTypes.bool
};

export { ContentIcon as default };
